import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RequestIndicatorTypes } from 'data-layer-cr/dist/models';
import { defaultValue as defaultIdValue } from 'src/constants/idTypes';
import { me } from 'src/constants/paymentIssuerSource';
import EmissionDataFields, {
  LegalBeneficiary,
  makeEmptyEmissionDataFields,
} from 'src/models/helperTypes/EmissionDataFields';
import FinalBeneficiary, {
  makeEmptyFinalBeneficiary,
} from 'src/models/helperTypes/FinalBeneficiary';
import { Fraction } from 'src/models/helperTypes/FractionationType';
import PaymentIssuer, {
  makeEmptyPaymentIssuer,
} from 'src/models/helperTypes/PaymentIssuer';

export type TotalsPayload = {
  paymentTotal: number;
  electronicPaymentTotal: number;
  autoPxPaymentTotal: number;
};

export type PaymentDataFields = {
  type: string;
  paymentQuantity: number;
  paymentQuantityType: string;
  paymentAmount: number;
  policyStartDate: string;
  paymentDayNumber: string;
};

export type EmissionPolicyState = {
  additionalDocumentACHFileName: string;
  // Base64 representations for managed images
  additionalDocumentACHBase64: string;

  // Base64 representations for managed images
  identificationFileName: string;
  identificationBase64: string;
  newCarInvoiceFilename: string;
  newCarInvoiceBase64: string;
  identificationOptionalFileName: string;
  identificationOptionalBase64: string;

  // Totals
  paymentTotal: number;
  electronicPaymentTotal: number;
  autoPxPaymentTotal: number;

  // Payments to show on screen
  annualPaymentTotals: TotalsPayload;
  monthlyPaymentTotals: TotalsPayload;

  // Fields
  dataFields: EmissionDataFields;
  // only filled when Acsel returns a non-existing legal entity profile
  finalBeneficiary: FinalBeneficiary;
  paymentFields: PaymentDataFields;
  paymentIssuerSource: string;
  paymentIssuer: PaymentIssuer;

  // The error code returned by the server in `create emission` transaction
  emissionErrorCode: string;
  emissionErrorDetail: string;
  emissionErrorDetailDescription: string;
  emissionRequestIndicator: RequestIndicatorTypes;
  showPaymentAlert: boolean;
  showSignAlert: boolean;

  /**
   * This flag will specify if the emission creation is a "retry" after the server
   * responds either "payment denied" o "payment failed". Note that this logic should
   * not be necessary, but since the server doesn't update the emission's last detected
   * state from "payment failed" when a fail occurs, we have no way of knowing if the
   * response fails because of a retry or because of a previous attempt. This will
   * help us decide if we need to stop retrying when a "payment failed" is detected.
   */
  isPaymentRetry: boolean;

  // track emission status for yappy payment
  emissionStatusCode: string;

  // Property for set validity date when exist policy on ANU status
  exclusionDate: string;

  // Evicertia Signature Link
  evicertiaLink: string;

  // Order id
  emissionOrderId: string;

  // Payment fractionations
  fractionations: Fraction[];

  // Legal beneficiaries
  legalBeneficiaries?: LegalBeneficiary[];
};

/**
 * We use a getter for initialState rather than an exported one because
 * paymentDayNumber needs to be dynamic.
 *
 * InitialStates are loaded in as soon as the imports are read by the test file,
 * so any attempt to mock will not be reflected. This is why a getter that would
 * trigger the conditional logic is necesary in order to reflect the mocks
 */
export function getInitialState(): EmissionPolicyState {
  // paymentDatNumber defaults to the current day number
  const currentDate = new Date(Date.now()).getDate().toString();

  // can only accept dates 1 through 30
  const usedDate = currentDate === '31' ? '30' : currentDate;
  return {
    additionalDocumentACHBase64: '',
    additionalDocumentACHFileName: '',
    identificationFileName: '',
    identificationBase64: '',
    newCarInvoiceFilename: '',
    identificationOptionalBase64: '',
    identificationOptionalFileName: '',
    newCarInvoiceBase64: '',
    paymentTotal: 0,
    electronicPaymentTotal: 0,
    autoPxPaymentTotal: 0,
    annualPaymentTotals: {
      paymentTotal: 0,
      electronicPaymentTotal: 0,
      autoPxPaymentTotal: 0,
    },
    monthlyPaymentTotals: {
      paymentTotal: 0,
      electronicPaymentTotal: 0,
      autoPxPaymentTotal: 0,
    },
    dataFields: makeEmptyEmissionDataFields(),
    finalBeneficiary: makeEmptyFinalBeneficiary(),
    paymentIssuerSource: me,
    paymentIssuer: {
      ...makeEmptyPaymentIssuer(),
      idType: defaultIdValue,
    },
    paymentFields: {
      type: 'DCL',
      paymentQuantity: 12,
      paymentQuantityType: 'anual',
      paymentAmount: 0,
      policyStartDate: '',
      paymentDayNumber: usedDate,
    },
    emissionErrorCode: '',
    emissionErrorDetail: '',
    emissionErrorDetailDescription: '',
    isPaymentRetry: false,
    emissionStatusCode: '',
    exclusionDate: '',
    evicertiaLink: '',
    emissionOrderId: '',
    showPaymentAlert: false,
    showSignAlert: true,
    emissionRequestIndicator: 'initial',

    fractionations: [],
    legalBeneficiaries: [],
  };
}

const emissionPolicySlice = createSlice({
  name: 'emissionPolicy',
  initialState: getInitialState(),
  reducers: {
    overwriteEmissionPolicy: (
      _,
      action: PayloadAction<EmissionPolicyState>,
    ): EmissionPolicyState => {
      return {
        ...action.payload,
        fractionations: [],
        showPaymentAlert: false,
        showSignAlert: true,
        emissionRequestIndicator: 'initial',
      };
    },
    updateAdditionalDocumentACHBase64: (
      state,
      action: PayloadAction<string>,
    ): void => {
      state.additionalDocumentACHBase64 = action.payload;
    },
    updateAdditionalDocumentACHFileName: (
      state,
      action: PayloadAction<string>,
    ): void => {
      state.additionalDocumentACHFileName = action.payload;
    },
    updateIdentificationBase64: (
      state,
      action: PayloadAction<string>,
    ): void => {
      state.identificationBase64 = action.payload;
    },
    updateIdentificationFileName: (
      state,
      action: PayloadAction<string>,
    ): void => {
      state.identificationFileName = action.payload;
    },
    updateIdentificationOptionalBase64: (
      state,
      action: PayloadAction<string>,
    ): void => {
      state.identificationOptionalBase64 = action.payload;
    },
    updateIdentificationOptionalFileName: (
      state,
      action: PayloadAction<string>,
    ): void => {
      state.identificationOptionalFileName = action.payload;
    },
    updateNewCarInvoiceFilename: (
      state,
      action: PayloadAction<string>,
    ): void => {
      state.newCarInvoiceFilename = action.payload;
    },
    updateNewCarInvoiceBase64: (state, action: PayloadAction<string>): void => {
      state.newCarInvoiceBase64 = action.payload;
    },
    updateDataFields: (
      state,
      action: PayloadAction<EmissionDataFields>,
    ): void => {
      state.dataFields = action.payload;
    },
    updateNewDataFields: (
      state,
      action: PayloadAction<Partial<EmissionDataFields>>,
    ): void => {
      state.dataFields = { ...state.dataFields, ...action.payload };
    },
    updateNewPayerDataFields: (
      state,
      action: PayloadAction<Partial<PaymentIssuer>>,
    ): void => {
      state.paymentIssuer = { ...state.paymentIssuer, ...action.payload };
    },
    updateFinalBeneficiary: (
      state,
      action: PayloadAction<FinalBeneficiary>,
    ): void => {
      state.finalBeneficiary = action.payload;
    },
    updatePaymentType: (state, action: PayloadAction<string>): void => {
      state.paymentFields.type = action.payload;
    },

    updatePaymentQuantityType: (state, action: PayloadAction<string>): void => {
      state.paymentFields.paymentQuantityType = action.payload;
    },
    updatePaymentQuantity: (state, action: PayloadAction<number>): void => {
      state.paymentFields.paymentQuantity = action.payload;
    },
    updatePaymentAmount: (state, action: PayloadAction<number>): void => {
      state.paymentFields.paymentAmount = action.payload;
    },

    updatePolicyStartDate: (state, action: PayloadAction<string>): void => {
      state.paymentFields.policyStartDate = action.payload;
    },

    updatePaymentDayNumber: (state, action: PayloadAction<string>): void => {
      state.paymentFields.paymentDayNumber = action.payload;
    },

    updateTotalPayments: (
      state,
      action: PayloadAction<TotalsPayload>,
    ): void => {
      state.paymentTotal = action.payload.paymentTotal;
      state.electronicPaymentTotal = action.payload.electronicPaymentTotal;
      state.autoPxPaymentTotal = action.payload.autoPxPaymentTotal;
    },

    updateAnnualPayments: (
      state,
      action: PayloadAction<TotalsPayload>,
    ): void => {
      state.annualPaymentTotals = {
        paymentTotal: action.payload.paymentTotal,
        electronicPaymentTotal: action.payload.electronicPaymentTotal,
        autoPxPaymentTotal: action.payload.autoPxPaymentTotal,
      };
    },

    updateMonthlyPayments: (
      state,
      action: PayloadAction<TotalsPayload>,
    ): void => {
      state.monthlyPaymentTotals = {
        paymentTotal: action.payload.paymentTotal,
        electronicPaymentTotal: action.payload.electronicPaymentTotal,
        autoPxPaymentTotal: action.payload.autoPxPaymentTotal,
      };
    },

    updateEmissionErrorCode: (state, action: PayloadAction<string>): void => {
      state.emissionErrorCode = action.payload;
    },

    updateEmissionErrorDetail: (state, action: PayloadAction<string>): void => {
      state.emissionErrorDetail = action.payload;
    },

    updateEmissionErrorDetailDescription: (
      state,
      action: PayloadAction<string>,
    ): void => {
      state.emissionErrorDetailDescription = action.payload;
    },

    updateShowPaymentAlert: (state, action: PayloadAction<boolean>): void => {
      state.showPaymentAlert = action.payload;
    },

    updateShowSignAlert: (state, action: PayloadAction<boolean>): void => {
      state.showSignAlert = action.payload;
    },

    updateEmissionRequestIndicator: (
      state,
      action: PayloadAction<RequestIndicatorTypes>,
    ): void => {
      state.emissionRequestIndicator = action.payload;
    },

    updateIsPaymentRetry: (state, action: PayloadAction<boolean>): void => {
      state.isPaymentRetry = action.payload;
    },
    updatePaymentIssuerSource: (state, action: PayloadAction<string>): void => {
      state.paymentIssuerSource = action.payload;
    },
    updatePaymentIssuer: (
      state,
      action: PayloadAction<PaymentIssuer>,
    ): void => {
      state.paymentIssuer = action.payload;
    },
    updateEmissionStatusCode: (state, action: PayloadAction<string>): void => {
      state.emissionStatusCode = action.payload;
    },
    updateExclusionDate: (state, action: PayloadAction<string>): void => {
      state.exclusionDate = action.payload;
    },
    updateEvicertiaLink: (state, action: PayloadAction<string>): void => {
      state.evicertiaLink = action.payload;
    },
    updateEmissionOrderId: (state, action: PayloadAction<string>): void => {
      state.emissionOrderId = action.payload;
    },
    updateFractionation: (state, action: PayloadAction<Fraction[]>): void => {
      state.fractionations = action.payload;
    },
    updateLegalBeneficiaries: (
      state,
      action: PayloadAction<LegalBeneficiary[]>,
    ): void => {
      state.legalBeneficiaries = action.payload;
    },
  },
});

export const {
  overwriteEmissionPolicy,
  updateAdditionalDocumentACHFileName,
  updateAdditionalDocumentACHBase64,
  updateIdentificationFileName,
  updateIdentificationBase64,
  updateIdentificationOptionalBase64,
  updateIdentificationOptionalFileName,
  updateNewCarInvoiceFilename,
  updateNewCarInvoiceBase64,
  updateDataFields,
  updateNewDataFields,
  updateNewPayerDataFields,
  updateFinalBeneficiary,
  updatePaymentType,
  updatePaymentQuantityType,
  updatePaymentQuantity,
  updatePaymentAmount,
  updatePolicyStartDate,
  updatePaymentDayNumber,
  updateTotalPayments,
  updateAnnualPayments,
  updateMonthlyPayments,
  updateEmissionErrorCode,
  updateEmissionErrorDetail,
  updateEmissionErrorDetailDescription,
  updateIsPaymentRetry,
  updatePaymentIssuerSource,
  updatePaymentIssuer,
  updateEmissionStatusCode,
  updateExclusionDate,
  updateEvicertiaLink,
  updateEmissionOrderId,
  updateFractionation,
  updateShowPaymentAlert,
  updateShowSignAlert,
  updateEmissionRequestIndicator,
  updateLegalBeneficiaries,
} = emissionPolicySlice.actions;

export default emissionPolicySlice.reducer;
